import React, { useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Ethnicities } from "../../constants";

import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, InputLabel, FormControl, MenuItem, TextField, Select, OutlinedInput } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

import { editProfile } from "../../actions/accountActions";


export const ProfileEditDialog = (params) => (
    <Dialog
        open={params.open}
        onClose={params.onClose}
        fullWidth
        maxWidth="md"
    >
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent>
            <Box
                component="form"
                autoComplete="off"
                noValidate
                className="dialog-content"
            >
                <FormControl component="div" margin="normal">
                    <InputLabel id="label-title" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                        Title
                    </InputLabel>
                    <OutlinedInput
                        id="text-title"
                        value={params.title}
                        onChange={({target}) => params.setTitle(target?.value)}
                        inputProps={{maxLength: 12}}
                    />
                </FormControl>
                <Box className="flex-container">
                    <FormControl
                        component="div"
                        margin="normal"
                        required
                        error={!params?.firstName}
                    >
                        <InputLabel id="label-firstname" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            First name
                        </InputLabel>
                        <OutlinedInput
                            id="text-firstname"
                            value={params.firstName}
                            onChange={({target}) => params.setFirstName(target?.value)}
                        />
                    </FormControl>
                    <FormControl component="div" margin="normal">
                        <InputLabel id="label-initials" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Initials
                        </InputLabel>
                        <OutlinedInput
                            id="text-initials"
                            value={params.initials}
                            onChange={({target}) => params.setInitials(target?.value)}
                            inputProps={{maxLength: 10}}
                        />
                    </FormControl>
                    <FormControl
                        component="div"
                        margin="normal"
                        required
                        error={!params?.lastName}
                    >
                        <InputLabel id="label-lastname" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Last name
                        </InputLabel>
                        <OutlinedInput
                            id="text-lastname"
                            value={params.lastName}
                            onChange={({target}) => params.setLastName(target?.value)}
                        />
                    </FormControl>
                </Box>
                <FormControl
                    component="div"
                    margin="normal"
                    fullWidth
                    required
                    error={!params?.email}
                >
                    <InputLabel id="label-area" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                        Email
                    </InputLabel>
                    <OutlinedInput
                        id="text-email"
                        value={params.email}
                        onChange={({target}) => params.setEmail(target?.value)}
                    />
                </FormControl>
                <FormControl component="div" margin="normal" fullWidth>
                    <InputLabel id="label-jobrole" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                        Job role
                    </InputLabel>
                    <OutlinedInput
                        id="text-jobrole"
                        value={params.jobRole}
                        onChange={({target}) => params.setJobRole(target?.value)}
                    />
                </FormControl>
                <FormControl component="div" margin="normal" fullWidth>
                    <InputLabel id="label-areaOfWork" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                        Area of work
                    </InputLabel>
                    <OutlinedInput
                        id="text-areaOfWork"
                        value={params.areaOfWork}
                        onChange={({target}) => params.setAreaOfWork(target?.value)}
                    />
                </FormControl>
                <Box className="flex-container">
                    <FormControl component="div" margin="normal">
                        <DatePicker
                            id="dob"
                            disableFuture
                            label="Date of birth"
                            format="DD/MM/YYYY"
                            value={dayjs(params.dob)}
                            onChange={(dob) => params.setDOB(dob.toString())}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </FormControl>
                    <FormControl component="div" margin="normal">
                        <InputLabel id="label-gender" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Gender
                        </InputLabel>
                        <Select
                            id="select-gender"
                            value={params.gender}
                            onChange={({target}) => params.setGender(target.value)}
                        >
                            {[
                                { label: "Male", value: "m" },
                                { label: "Female", value: "f" },
                                { label: "N/A", value: "n/a" },
                            ].map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl component="div" margin="normal">
                        <InputLabel id="label-gender" sx={{backgroundColor: "#FFF", padding: "0 4px"}}>
                            Ethnicity
                        </InputLabel>
                        <Select
                            id="select-ethnicity"
                            value={params.ethnicity}
                            onChange={({target}) => params.setEthnicity(target.value)}
                        >
                            {Ethnicities.map(({code, description}) => (
                                <MenuItem key={code} value={code}>{`${code} - ${description}`}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>
        </DialogContent>
        <DialogActions>
            <Button key="cancel" onClick={params.onClose}>
                Cancel
            </Button>
            <Button
                key="save"
                variant="contained"
                color="primary"
                onClick={() => {
                    params.onSave();
                    params.onClose();
                }}
                disableElevation={true}
                disabled={!params.firstName || !params.lastName || !params.email}
            >
                Save
            </Button>
        </DialogActions>
    </Dialog>
);

const ProfileEditDialogContainer = ({ open, profile, onClose, ...others }) => {
    const dispatch = useDispatch();

    const [title, setTitle] = useState(profile?.title || "");
    const [initials, setInitials] = useState(profile?.initials || "");
    const [firstName, setFirstName] = useState(profile?.firstName || "");
    const [lastName, setLastName] = useState(profile?.lastName || "");
    const [email, setEmail] = useState(profile?.email || "");
    const [jobRole, setJobRole] = useState(profile?.jobRole || "");
    const [areaOfWork, setAreaOfWork] = useState(profile?.areaOfWork || "");
    const [dob, setDOB] = useState(profile?.dob || null);
    const [gender, setGender] = useState(profile?.gender || "n/a");
    const [ethnicity, setEthnicity] = useState(profile?.ethnicity || "NS");

    const handleSubmit = () =>
        dispatch(
            editProfile({
                title,
                firstName,
                initials,
                lastName,
                email,
                jobRole,
                areaOfWork,
                dob,
                gender: gender === "n/a" ? "" : gender,
                ethnicity
            })
        );

    return (
        <ProfileEditDialog
            open={open}
            title={title}
            setTitle={setTitle}
            firstName={firstName}
            setFirstName={setFirstName}
            initials={initials}
            setInitials={setInitials}
            lastName={lastName}
            setLastName={setLastName}
            email={email}
            setEmail={setEmail}
            jobRole={jobRole}
            setJobRole={setJobRole}
            areaOfWork={areaOfWork}
            setAreaOfWork={setAreaOfWork}
            dob={dob}
            setDOB={setDOB}
            gender={gender}
            setGender={setGender}
            ethnicity={ethnicity}
            setEthnicity={setEthnicity}
            onClose={onClose}
            onSave={handleSubmit}
            {...others}
        />
    );
};

ProfileEditDialogContainer.propTypes = {
    open: PropTypes.bool,
    profile: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        email: PropTypes.string,
        jobRole: PropTypes.string,
        areaOfWork: PropTypes.string,
        dob: PropTypes.string,
        gender: PropTypes.string,
    }),
    onClose: PropTypes.func,
};

ProfileEditDialogContainer.defaultProps = {
    open: false,
    onClose: () => {},
};

export default ProfileEditDialogContainer;
